import { useState, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AnimatedCounter } from '@/components/motion';
import { evaluateFormula } from '@/features/pricing-models/utils/formula-evaluator';
import { MODELO_SOFTWARE, DEMO_SCENARIO } from '../data/tutorial-data';
import { cn } from '@/lib/utils/cn';

const INPUT_VARS = MODELO_SOFTWARE.variables.filter((v) => v.variableType !== 'CALCULATED');
const CALC_VARS = MODELO_SOFTWARE.variables.filter((v) => v.variableType === 'CALCULATED');

export function FormulaPlayground() {
  const [inputs, setInputs] = useState<Record<string, number>>({ ...DEMO_SCENARIO.inputs });

  // Calcular todas las variables CALCULATED en cascada
  const calculated = useMemo(() => {
    const values: Record<string, number> = { ...inputs };
    const results: Record<string, number> = {};

    for (const v of CALC_VARS) {
      if (!v.formula) continue;
      const result = evaluateFormula(v.formula, values);
      const val = typeof result.value === 'number' ? result.value : 0;
      values[v.code] = val;
      results[v.code] = val;
    }

    return results;
  }, [inputs]);

  const updateInput = (code: string, value: number) => {
    setInputs((prev) => ({ ...prev, [code]: value }));
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Cambia cualquier valor de entrada y observa como{' '}
        <strong className="text-foreground">todas las formulas se recalculan en cascada</strong>.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input variables */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline" className="font-mono text-xs">
                ENTRADA
              </Badge>
              <span className="text-sm font-medium">Variables editables</span>
            </div>
            {INPUT_VARS.map((v) => (
              <div key={v.code} className="space-y-1">
                <label className="text-xs text-muted-foreground flex items-center gap-2">
                  <code className="text-primary font-mono">{v.code}</code>
                  <span>— {v.name}</span>
                </label>
                <input
                  type="number"
                  value={inputs[v.code] ?? v.defaultValue ?? 0}
                  onChange={(e) => updateInput(v.code, parseFloat(e.target.value) || 0)}
                  className={cn(
                    'w-full px-3 py-2 rounded-md border bg-background text-sm font-mono',
                    'focus:outline-none focus:ring-2 focus:ring-primary/50'
                  )}
                  step={
                    v.variableType === 'PERCENTAGE' ? 1 : v.variableType === 'CURRENCY' ? 0.01 : 1
                  }
                />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Calculated variables */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Badge className="font-mono text-xs bg-primary">CALCULADA</Badge>
              <span className="text-sm font-medium">Resultados en vivo</span>
            </div>
            {CALC_VARS.map((v) => {
              const value = calculated[v.code] ?? 0;
              return (
                <div key={v.code} className="bg-muted/50 rounded-lg p-3 space-y-1">
                  <div className="flex items-center justify-between">
                    <div>
                      <code className="text-xs text-primary font-mono">{v.code}</code>
                      <p className="text-xs text-muted-foreground">{v.name}</p>
                    </div>
                    <span className="text-lg font-bold font-mono">
                      <AnimatedCounter target={value} decimals={2} prefix="$ " duration={0.6} />
                    </span>
                  </div>
                  <p className="text-[10px] text-muted-foreground font-mono bg-muted px-2 py-1 rounded">
                    = {v.formula}
                  </p>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
